/**
 * useReceivedGifts Hook - Gift Reception & Notification Management
 *
 * Manages received gift sessions for the current user.
 * Provides:
 * - List of all received gifts with viewing status
 * - Unread gift count for notification badges
 * - Mark gift as viewed functionality
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/utils/apiClient';

export interface ReceivedGift {
  id: string;
  title: string;
  audio_url: string;
  duration: number | null;
  category_id: string;
  sender_name: string | null;
  gift_message: string | null;
  background_music_file: string | null;
  created_at: string;
  gift_viewed: boolean;
  gift_viewed_at: string | null;
}

interface UnreadCountResponse {
  unread_count: number;
}

interface MarkViewedResponse {
  success: boolean;
  viewed_at: string;
}

export function useReceivedGifts() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all received gifts
  const giftsQuery = useQuery({
    queryKey: ['received-gifts', user?.id],
    queryFn: async () => {
      const response = await apiClient.get('/sessions/received-gifts');
      return (response || []) as ReceivedGift[];
    },
    enabled: !!user?.id,
  });

  // Fetch unread count for notification badge
  const unreadCountQuery = useQuery({
    queryKey: ['received-gifts-unread-count', user?.id],
    queryFn: async () => {
      const response = await apiClient.get('/sessions/received-gifts/unread-count') as UnreadCountResponse;
      return response.unread_count;
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    refetchInterval: 60 * 1000, // Poll every 60 seconds
    refetchOnWindowFocus: true,
  });

  // Mark a gift as viewed
  const markViewedMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      return apiClient.post(`/sessions/received-gifts/${sessionId}/mark-viewed`, {}) as Promise<MarkViewedResponse>;
    },
    onSuccess: () => {
      // Invalidate both queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['received-gifts-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['received-gifts'] });
    },
  });

  return {
    // Gift data
    gifts: giftsQuery.data || [],
    isLoading: giftsQuery.isLoading,
    error: giftsQuery.error,

    // Unread count for badges
    unreadCount: unreadCountQuery.data ?? 0,
    unreadLoading: unreadCountQuery.isLoading,

    // Actions
    markAsViewed: markViewedMutation.mutate,
    markAsViewedAsync: markViewedMutation.mutateAsync,
    isMarkingViewed: markViewedMutation.isPending,

    // Refetch functions
    refetchGifts: giftsQuery.refetch,
    refetchUnreadCount: unreadCountQuery.refetch,
  };
}

export default useReceivedGifts;
